
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Save, Settings, Facebook, BarChart3, Code, MessageSquare, MapPin, Eye, EyeOff, KeyRound, Mail, Send } from "lucide-react";
import { phpApi } from "@/integrations/php/client";

const SiteSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [showGoogleKey, setShowGoogleKey] = useState(false);
  const [showPartnerPassword, setShowPartnerPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState("");
  const [settings, setSettings] = useState({
    id: "",
    fbPixelId: "",
    gaId: "",
    twilioSid: "",
    twilioAuthToken: "",
    twilioMessagingSid: "",
    googleMapsApiKey: "",
    partnerRegistrationPassword: "",
    smtpHost: "",
    smtpPort: "465",
    smtpUsername: "",
    smtpPassword: "",
    smtpFromName: "Leads System",
    smtpFromEmail: "",
    smtpEncryption: "ssl",
  });
  const { toast } = useToast();

  const getApiBaseUrl = (): string => {
    if (import.meta.env.DEV) return '';
    return import.meta.env.VITE_API_BASE_URL || '';
  };

  const handleTestEmail = async () => {
    const recipientEmail = (testEmailRecipient || settings.smtpFromEmail || settings.smtpUsername || '').trim();

    if (!recipientEmail) {
      toast({ title: "Σφάλμα", description: "Βάλε email παραλήπτη για το δοκιμαστικό.", variant: "destructive" });
      return;
    }

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail);
    if (!isValidEmail) {
      toast({ title: "Σφάλμα", description: "Το email δοκιμής δεν είναι έγκυρο.", variant: "destructive" });
      return;
    }

    setIsTestingEmail(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/send-email.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recipientEmail,
          leadId: 'smtp-test-' + Date.now(),
          leadDetails: {
            name: 'Δοκιμαστικός Χρήστης',
            service: 'Δοκιμή SMTP',
            phone: '6900000000',
            address: 'Αθήνα, Ελλάδα',
            timeframe: 'Άμεσα',
            comments: 'Αυτό είναι ένα δοκιμαστικό email για επαλήθευση ρυθμίσεων SMTP.'
          },
          emailType: 'partner_notification'
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok && result.status === 'success') {
        toast({ title: "Επιτυχία!", description: `Δοκιμαστικό email εστάλη στο ${recipientEmail}`, variant: "default" });
      } else {
        toast({ title: "Αποτυχία SMTP", description: result.message || 'Σφάλμα κατά την αποστολή', variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Σφάλμα", description: error instanceof Error ? error.message : 'Αδυναμία σύνδεσης', variant: "destructive" });
    } finally {
      setIsTestingEmail(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);

        const { data, error } = await phpApi
          .from('site_settings')
          .select('*')
          .single();

        if (error) {
          console.error('Error fetching settings:', error);
          if (error.code === 'PGRST116') {
            const { data: newData, error: insertError } = await phpApi
              .from('site_settings')
              .insert([{ fb_pixel_id: '', ga_id: '', twilio_sid: '', twilio_auth_token: '', twilio_messaging_sid: '', google_maps_api_key: '', partner_registration_password: '!Partner25', smtp_host: '', smtp_port: 465, smtp_username: '', smtp_password: '', smtp_from_name: 'Leads System', smtp_from_email: '', smtp_encryption: 'ssl' }])
              .select()
              .single();

            if (insertError) throw insertError;

            setSettings({
              id: newData.id,
              fbPixelId: newData.fb_pixel_id || '',
              gaId: newData.ga_id || '',
              twilioSid: newData.twilio_sid || '',
              twilioAuthToken: newData.twilio_auth_token || '',
              twilioMessagingSid: newData.twilio_messaging_sid || '',
              googleMapsApiKey: newData.google_maps_api_key || '',
              partnerRegistrationPassword: newData.partner_registration_password || '',
              smtpHost: newData.smtp_host || '',
              smtpPort: String(newData.smtp_port || 465),
              smtpUsername: newData.smtp_username || '',
              smtpPassword: newData.smtp_password || '',
              smtpFromName: newData.smtp_from_name || 'Leads System',
              smtpFromEmail: newData.smtp_from_email || '',
              smtpEncryption: newData.smtp_encryption || 'ssl',
            });
            return;
          }

          throw error;
        }

        if (data) {
          setSettings({
            id: data.id,
            fbPixelId: data.fb_pixel_id || '',
            gaId: data.ga_id || '',
            twilioSid: data.twilio_sid || '',
            twilioAuthToken: data.twilio_auth_token || '',
            twilioMessagingSid: data.twilio_messaging_sid || '',
            googleMapsApiKey: data.google_maps_api_key || '',
            partnerRegistrationPassword: data.partner_registration_password || '',
            smtpHost: data.smtp_host || '',
            smtpPort: String(data.smtp_port || 465),
            smtpUsername: data.smtp_username || '',
            smtpPassword: data.smtp_password || '',
            smtpFromName: data.smtp_from_name || 'Leads System',
            smtpFromEmail: data.smtp_from_email || '',
            smtpEncryption: data.smtp_encryption || 'ssl',
          });
        }
      } catch (error) {
        console.error('Error:', error);
        toast({
          title: "Σφάλμα",
          description: "Υπήρξε πρόβλημα κατά την ανάκτηση των ρυθμίσεων.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [toast]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSaving(true);

      const { error } = await phpApi
        .from('site_settings')
        .update({
          fb_pixel_id: settings.fbPixelId,
          ga_id: settings.gaId,
          twilio_sid: settings.twilioSid,
          twilio_auth_token: settings.twilioAuthToken,
          twilio_messaging_sid: settings.twilioMessagingSid,
          google_maps_api_key: settings.googleMapsApiKey,
          partner_registration_password: settings.partnerRegistrationPassword,
          smtp_host: settings.smtpHost,
          smtp_port: parseInt(settings.smtpPort) || 465,
          smtp_username: settings.smtpUsername,
          smtp_password: settings.smtpPassword,
          smtp_from_name: settings.smtpFromName,
          smtp_from_email: settings.smtpFromEmail,
          smtp_encryption: settings.smtpEncryption,
        })
        .eq('id', settings.id);

      if (error) throw error;

      toast({
        title: "Επιτυχία",
        description: "Οι ρυθμίσεις αποθηκεύτηκαν με επιτυχία.",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Σφάλμα",
        description: "Υπήρξε πρόβλημα κατά την αποθήκευση των ρυθμίσεων.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-blue-500/10 animate-ping"></div>
            </div>
            <Loader2 className="relative h-10 w-10 animate-spin text-blue-600" />
          </div>
          <p className="mt-4 text-slate-500">Φόρτωση ρυθμίσεων...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 shadow-lg">
          <Settings className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Ρυθμίσεις</h1>
          <p className="text-slate-500 mt-0.5">Διαμόρφωση ιστοσελίδας και tracking</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tracking Settings Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <Code className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Ρυθμίσεις Παρακολούθησης</CardTitle>
                <CardDescription>Facebook Pixel & Google Analytics</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    <Label htmlFor="fbPixelId" className="text-slate-700 font-medium">
                      Facebook Pixel ID
                    </Label>
                  </div>
                  <Input
                    id="fbPixelId"
                    name="fbPixelId"
                    value={settings.fbPixelId}
                    onChange={handleChange}
                    placeholder="π.χ. 1234567890123456"
                    className="bg-slate-50 border-slate-200"
                  />
                  <p className="text-xs text-slate-500">
                    Εισάγετε το ID του Facebook Pixel για την παρακολούθηση μετατροπών.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-amber-600" />
                    <Label htmlFor="gaId" className="text-slate-700 font-medium">
                      Google Analytics ID
                    </Label>
                  </div>
                  <Input
                    id="gaId"
                    name="gaId"
                    value={settings.gaId}
                    onChange={handleChange}
                    placeholder="π.χ. G-XXXXXXXXXX ή UA-XXXXXXXX-X"
                    className="bg-slate-50 border-slate-200"
                  />
                  <p className="text-xs text-slate-500">
                    Εισάγετε το ID του Google Analytics για την παρακολούθηση επισκεψιμότητας.
                  </p>
                </div>
              </div>

              <Button type="submit" disabled={isSaving} className="btn-gradient">
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Αποθήκευση...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Αποθήκευση Ρυθμίσεων
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Twilio SMS Settings Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-50">
                <MessageSquare className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Twilio SMS</CardTitle>
                <CardDescription>Ρυθμίσεις αποστολής SMS μέσω Twilio</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="twilioSid" className="text-slate-700 font-medium">
                    Account SID
                  </Label>
                  <Input
                    id="twilioSid"
                    name="twilioSid"
                    value={settings.twilioSid}
                    onChange={handleChange}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="bg-slate-50 border-slate-200 font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twilioAuthToken" className="text-slate-700 font-medium">
                    Auth Token
                  </Label>
                  <div className="relative">
                    <Input
                      id="twilioAuthToken"
                      name="twilioAuthToken"
                      type={showTwilioToken ? "text" : "password"}
                      value={settings.twilioAuthToken}
                      onChange={handleChange}
                      placeholder="••••••••••••••••••••••••••••••••"
                      className="bg-slate-50 border-slate-200 font-mono text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowTwilioToken(!showTwilioToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showTwilioToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twilioMessagingSid" className="text-slate-700 font-medium">
                    Messaging Service SID
                  </Label>
                  <Input
                    id="twilioMessagingSid"
                    name="twilioMessagingSid"
                    value={settings.twilioMessagingSid}
                    onChange={handleChange}
                    placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="bg-slate-50 border-slate-200 font-mono text-sm"
                  />
                </div>
              </div>

              <Button type="submit" disabled={isSaving} className="btn-gradient">
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Αποθήκευση...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Αποθήκευση Twilio
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Google Maps API Key Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-red-50">
                <MapPin className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Google Maps API</CardTitle>
                <CardDescription>Geocoding για αντιστοίχιση leads σε συνεργάτες</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="googleMapsApiKey" className="text-slate-700 font-medium">
                  API Key
                </Label>
                <div className="relative">
                  <Input
                    id="googleMapsApiKey"
                    name="googleMapsApiKey"
                    type={showGoogleKey ? "text" : "password"}
                    value={settings.googleMapsApiKey}
                    onChange={handleChange}
                    placeholder="AIzaSy..."
                    className="bg-slate-50 border-slate-200 font-mono text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowGoogleKey(!showGoogleKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showGoogleKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Απαιτείται για geocoding (μετατροπή ΤΚ/πόλης σε συντεταγμένες) κατά την ανάθεση leads.
                </p>
              </div>

              <Button type="submit" disabled={isSaving} className="btn-gradient">
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Αποθήκευση...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Αποθήκευση Google Maps
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Partner Registration Password Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-purple-50">
                <KeyRound className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Κωδικός Εγγραφής Συνεργατών</CardTitle>
                <CardDescription>Κωδικός πρόσβασης για τη σελίδα εγγραφής νέων συνεργατών</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="partnerRegistrationPassword" className="text-slate-700 font-medium">
                  Κωδικός Πρόσβασης
                </Label>
                <div className="relative">
                  <Input
                    id="partnerRegistrationPassword"
                    name="partnerRegistrationPassword"
                    type={showPartnerPassword ? "text" : "password"}
                    value={settings.partnerRegistrationPassword}
                    onChange={handleChange}
                    placeholder="Εισάγετε κωδικό πρόσβασης"
                    className="bg-slate-50 border-slate-200 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPartnerPassword(!showPartnerPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPartnerPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Αυτός ο κωδικός απαιτείται για την πρόσβαση στη φόρμα εγγραφής συνεργατών στο /partner/register.
                </p>
              </div>

              <Button type="submit" disabled={isSaving} className="btn-gradient">
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Αποθήκευση...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Αποθήκευση Κωδικού
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Email SMTP Settings Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-sky-50">
                <Mail className="h-4 w-4 text-sky-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Email SMTP</CardTitle>
                <CardDescription>Ρυθμίσεις αποστολής email μέσω SMTP</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtpHost" className="text-slate-700 font-medium">
                      SMTP Host
                    </Label>
                    <Input
                      id="smtpHost"
                      name="smtpHost"
                      value={settings.smtpHost}
                      onChange={handleChange}
                      placeholder="mail.example.com"
                      className="bg-slate-50 border-slate-200 font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtpPort" className="text-slate-700 font-medium">
                      Port
                    </Label>
                    <Input
                      id="smtpPort"
                      name="smtpPort"
                      value={settings.smtpPort}
                      onChange={handleChange}
                      placeholder="465"
                      className="bg-slate-50 border-slate-200 font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtpUsername" className="text-slate-700 font-medium">
                    Username / Email
                  </Label>
                  <Input
                    id="smtpUsername"
                    name="smtpUsername"
                    value={settings.smtpUsername}
                    onChange={handleChange}
                    placeholder="no-reply@example.com"
                    className="bg-slate-50 border-slate-200 font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtpPassword" className="text-slate-700 font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="smtpPassword"
                      name="smtpPassword"
                      type={showSmtpPassword ? "text" : "password"}
                      value={settings.smtpPassword}
                      onChange={handleChange}
                      placeholder="••••••••••"
                      className="bg-slate-50 border-slate-200 font-mono text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showSmtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtpFromName" className="text-slate-700 font-medium">
                      Όνομα Αποστολέα
                    </Label>
                    <Input
                      id="smtpFromName"
                      name="smtpFromName"
                      value={settings.smtpFromName}
                      onChange={handleChange}
                      placeholder="Leads System"
                      className="bg-slate-50 border-slate-200 text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtpFromEmail" className="text-slate-700 font-medium">
                      Email Αποστολέα
                    </Label>
                    <Input
                      id="smtpFromEmail"
                      name="smtpFromEmail"
                      value={settings.smtpFromEmail}
                      onChange={handleChange}
                      placeholder="no-reply@example.com"
                      className="bg-slate-50 border-slate-200 font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtpEncryption" className="text-slate-700 font-medium">
                    Κρυπτογράφηση
                  </Label>
                  <select
                    id="smtpEncryption"
                    name="smtpEncryption"
                    value={settings.smtpEncryption}
                    onChange={(e) => setSettings(prev => ({ ...prev, smtpEncryption: e.target.value }))}
                    className="w-full h-10 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm"
                  >
                    <option value="ssl">SSL (Port 465)</option>
                    <option value="tls">TLS (Port 587)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="testEmailRecipient" className="text-slate-700 font-medium">
                    Email Δοκιμής
                  </Label>
                  <Input
                    id="testEmailRecipient"
                    name="testEmailRecipient"
                    value={testEmailRecipient}
                    onChange={(e) => setTestEmailRecipient(e.target.value)}
                    placeholder={settings.smtpFromEmail || settings.smtpUsername || "test@example.com"}
                    className="bg-slate-50 border-slate-200 font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">
                    Αν μείνει κενό, θα χρησιμοποιηθεί το Email Αποστολέα ή το Username SMTP.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={isSaving} className="btn-gradient">
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Αποθήκευση...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Αποθήκευση Email
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isTestingEmail}
                  onClick={handleTestEmail}
                  className="border-sky-300 text-sky-700 hover:bg-sky-50"
                >
                  {isTestingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Αποστολή...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Δοκιμαστικό Email
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-slate-200 bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Πληροφορίες</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-white/80 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">Twilio SMS</h4>
              <p className="text-sm text-slate-600">
                Τα κλειδιά Twilio χρησιμοποιούνται για την αποστολή SMS ειδοποιήσεων 
                στους συνεργάτες. Βρείτε τα στο{" "}
                <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Twilio Console
                </a>.
              </p>
            </div>

            <div className="p-4 bg-white/80 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">Google Maps API</h4>
              <p className="text-sm text-slate-600">
                Το κλειδί Google Maps χρησιμοποιείται για geocoding (ΤΚ → συντεταγμένες) 
                κατά τη δρομολόγηση leads. Ενεργοποιήστε το Geocoding API στο{" "}
                <a href="https://console.cloud.google.com/apis" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Google Cloud Console
                </a>.
              </p>
            </div>

            <div className="p-4 bg-white/80 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">Facebook Pixel</h4>
              <p className="text-sm text-slate-600">
                Το Facebook Pixel σας επιτρέπει να παρακολουθείτε τις ενέργειες των χρηστών
                στην ιστοσελίδα σας και να βελτιστοποιείτε τις διαφημίσεις σας.
              </p>
            </div>

            <div className="p-4 bg-white/80 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-2">Google Analytics</h4>
              <p className="text-sm text-slate-600">
                Το Google Analytics παρέχει λεπτομερή στατιστικά για την επισκεψιμότητα
                και τη συμπεριφορά των χρηστών στην ιστοσελίδα σας.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SiteSettings;
